import { AuditActionFilter, AuditTrailFilterState } from './buildAuditTrailRsql';

// Short keys keep a shared link readable.
const PARAM_KEYS = {
    search: 'q',
    action: 'action',
    entityTypes: 'types',
    authorIds: 'authors',
    changedAtFrom: 'from',
    changedAtTo: 'to',
    hideSystem: 'nosys',
    transactionId: 'tx',
} as const satisfies Record<keyof AuditTrailFilterState, string>;

const ACTIONS: readonly AuditActionFilter[] = ['All', 'Created', 'Updated', 'Deleted'];
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DIGITS = /^\d+$/;
const TABLE_NAME = /^[a-z][a-z0-9_]*$/;

function isAction(value: string | null): value is AuditActionFilter {
    return ACTIONS.includes(value as AuditActionFilter);
}

function splitList(value: string | null): string[] {
    return (value ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

function validOrNull(value: string | null, isValid: (value: string) => boolean): string | null {
    return value !== null && isValid(value) ? value : null;
}

/** A real UTC calendar day, not just the YYYY-MM-DD shape (rejects 2026-02-30 and 2026-13-01). */
function isCalendarDay(value: string): boolean {
    if (!DAY.test(value)) {
        return false;
    }
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}

/**
 * Tolerates hand-edited or stale links: anything malformed falls back to the unfiltered default.
 * The values end up inside the RSQL filter, so each one is checked against the shape the
 * backend grammar accepts rather than passed through verbatim.
 */
export function parseAuditTrailFilterParams(params: URLSearchParams): AuditTrailFilterState {
    const action = params.get(PARAM_KEYS.action);
    return {
        search: params.get(PARAM_KEYS.search) ?? '',
        action: isAction(action) ? action : 'All',
        entityTypes: splitList(params.get(PARAM_KEYS.entityTypes)).filter((type) => TABLE_NAME.test(type)),
        authorIds: splitList(params.get(PARAM_KEYS.authorIds))
            .filter((id) => DIGITS.test(id))
            .map(Number)
            .filter((id) => Number.isSafeInteger(id) && id > 0),
        changedAtFrom: validOrNull(params.get(PARAM_KEYS.changedAtFrom), isCalendarDay),
        changedAtTo: validOrNull(params.get(PARAM_KEYS.changedAtTo), isCalendarDay),
        hideSystem: params.get(PARAM_KEYS.hideSystem) !== '0',
        transactionId: validOrNull(params.get(PARAM_KEYS.transactionId), (id) => DIGITS.test(id)),
    };
}

/** Writes the filters over `base`, leaving any unrelated query params in place; defaults are omitted. */
export function buildAuditTrailFilterParams(
    filters: AuditTrailFilterState,
    base: URLSearchParams = new URLSearchParams()
): URLSearchParams {
    const params = new URLSearchParams(base);
    Object.values(PARAM_KEYS).forEach((key) => params.delete(key));
    if (filters.search) {
        params.set(PARAM_KEYS.search, filters.search);
    }
    if (filters.action !== 'All') {
        params.set(PARAM_KEYS.action, filters.action);
    }
    if (filters.entityTypes.length > 0) {
        params.set(PARAM_KEYS.entityTypes, filters.entityTypes.join(','));
    }
    if (filters.authorIds.length > 0) {
        params.set(PARAM_KEYS.authorIds, filters.authorIds.join(','));
    }
    if (filters.changedAtFrom) {
        params.set(PARAM_KEYS.changedAtFrom, filters.changedAtFrom);
    }
    if (filters.changedAtTo) {
        params.set(PARAM_KEYS.changedAtTo, filters.changedAtTo);
    }
    if (!filters.hideSystem) {
        params.set(PARAM_KEYS.hideSystem, '0');
    }
    if (filters.transactionId) {
        params.set(PARAM_KEYS.transactionId, filters.transactionId);
    }
    return params;
}
